import React from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  TextField,
  Button,
  Paper,
  useTheme,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  alpha,
  styled,
  InputAdornment,
  Stack,
} from "@mui/material";
import DataObjectIcon from "@mui/icons-material/DataObject";
import HubIcon from "@mui/icons-material/Hub";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import SendIcon from "@mui/icons-material/Send";
import PersonIcon from "@mui/icons-material/Person";
import PhoneIcon from "@mui/icons-material/Phone";
import MailIcon from "@mui/icons-material/Mail";

// Form Paper estilizado
const FormPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  backgroundColor: alpha(theme.palette.background.paper, 0.9),
  backdropFilter: "blur(10px)",
  border: `1px solid ${alpha(theme.palette.primary.light, 0.2)}`,
  borderRadius: theme.shape.borderRadius * 1.5,
  boxShadow: `0 0 30px ${alpha(theme.palette.primary.main, 0.15)}`,
}));

export const HeroSection = () => {
  const theme = useTheme();
  const [formData, setFormData] = React.useState({
    nome: "",
    telefone: "",
    email: "",
    tipoPlano: "",
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>
  ) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name as string]: value,
    });
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    console.log("Dados do formulário:", formData);
    // Lógica para enviar os dados
  };

  return (
    <Box
      sx={{
        py: { xs: 10, md: 15 },
        minHeight: "80vh",
        display: "flex",
        alignItems: "center",
        position: "relative",
        overflow: "hidden",
        background: `linear-gradient(160deg, ${
          theme.palette.background.default
        } 0%, ${alpha(theme.palette.primary.dark, 0.15)} 100%)`,
      }}
    >
      {/* Background effect (e.g., particles or network lines) could go here */}
      {/* Placeholder for a more complex background */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `radial-gradient(circle at 20% 30%, ${alpha(
            theme.palette.primary.main,
            0.1
          )} 0%, transparent 40%),
                       radial-gradient(circle at 80% 70%, ${alpha(
                         theme.palette.secondary.main,
                         0.08
                       )} 0%, transparent 50%)`,
          opacity: 0.5,
          zIndex: 0,
        }}
      />

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <Grid container spacing={6} alignItems="center">
          {/* Text Column */}
          <Grid item xs={12} md={7}>
            <Typography
              variant="overline"
              sx={{
                color: theme.palette.primary.light,
                mb: 1,
                display: "block",
              }}
            >
              Conectando você ao melhor plano
            </Typography>
            <Typography
              variant="h1"
              component="h1"
              sx={{
                fontWeight: 700,
                fontSize: { xs: "2.5rem", sm: "3rem", md: "3.8rem" },
                lineHeight: 1.2,
                mb: 3,
                color: theme.palette.text.primary,
                textShadow: `0 0 10px ${alpha(
                  theme.palette.primary.light,
                  0.2
                )}`,
              }}
            >
              Planos de Saúde Descomplicados com Tecnologia
            </Typography>
            <Typography
              variant="h6"
              component="p"
              sx={{
                mb: 4,
                color: theme.palette.text.secondary,
                fontWeight: 300,
                maxWidth: "550px",
              }}
            >
              Utilizamos análise de dados para encontrar a cobertura ideal para
              seu perfil, garantindo economia e a melhor rede de atendimento.
            </Typography>
            <Stack direction="row" spacing={2}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                href="#planos"
                sx={{ color: theme.palette.primary.contrastText }}
              >
                Ver Opções
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                size="large"
                href="#formulario"
              >
                Solicitar Contato
              </Button>
            </Stack>
          </Grid>

          {/* Form Column */}
          <Grid item xs={12} md={5} id="formulario">
            <FormPaper elevation={0}>
              <Typography
                variant="h5"
                component="h2"
                sx={{
                  fontWeight: 600,
                  mb: 3,
                  textAlign: "center",
                  color: theme.palette.primary.light,
                }}
              >
                Iniciar Simulação
              </Typography>

              <Box component="form" noValidate onSubmit={handleSubmit}>
                <TextField
                  fullWidth
                  label="Nome"
                  variant="outlined"
                  margin="dense"
                  required
                  name="nome"
                  value={formData.nome}
                  onChange={handleChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon
                          fontSize="small"
                          sx={{ color: theme.palette.text.secondary }}
                        />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Telefone"
                  variant="outlined"
                  margin="dense"
                  required
                  name="telefone"
                  value={formData.telefone}
                  onChange={handleChange}
                  type="tel"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneIcon
                          fontSize="small"
                          sx={{ color: theme.palette.text.secondary }}
                        />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="E-mail"
                  variant="outlined"
                  margin="dense"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  type="email"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <MailIcon
                          fontSize="small"
                          sx={{ color: theme.palette.text.secondary }}
                        />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mb: 2 }}
                />
                <FormControl fullWidth margin="dense" sx={{ mb: 3 }}>
                  <InputLabel id="tipo-plano-label-dark">
                    Tipo de Plano
                  </InputLabel>
                  <Select
                    labelId="tipo-plano-label-dark"
                    id="tipo-plano-dark"
                    name="tipoPlano"
                    value={formData.tipoPlano}
                    label="Tipo de Plano"
                    onChange={(e) =>
                      handleChange(e as React.ChangeEvent<HTMLInputElement>)
                    }
                    sx={{ backgroundColor: alpha("#ffffff", 0.05) }}
                  >
                    <MenuItem value="individual">Individual</MenuItem>
                    <MenuItem value="familiar">Familiar</MenuItem>
                    <MenuItem value="empresarial">Empresarial</MenuItem>
                  </Select>
                </FormControl>
                <Button
                  variant="contained"
                  fullWidth
                  size="large"
                  type="submit"
                  color="primary"
                  endIcon={<SendIcon />}
                  sx={{
                    py: 1.5,
                    color: theme.palette.primary.contrastText,
                  }}
                >
                  Enviar Dados
                </Button>
              </Box>
            </FormPaper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};
